import { keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import { SCIENT_COLORS } from "../../../../styled/scientColors"

export const FactsheetContainer = styled(FlexContainer)({
  padding: "16px",
  gap: "16px",
  overflowY: "auto",
  height: "100%",
})

export const GroupCardsGrid = styled.div({
  display: "flex",
  gap: "12px",
  width: "100%",
})

export const MasonryColumn = styled.div({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minWidth: 0, // Allow columns to shrink
})

export const SummaryRow = styled(FlexContainer)({
  gap: "16px",
  flexWrap: "wrap",
})

export const Card = styled(FlexContainer)({
  backgroundColor: SCIENT_COLORS.darkGray3,
  borderRadius: "8px",
  padding: "16px",
  minWidth: "300px",
  flex: 1,
  border: `1px solid ${SCIENT_COLORS.darkGray4}`,
})

export const CardTitle = styled.h3({
  margin: "0 0 12px 0",
  fontSize: "12px",
  fontWeight: 600,
  color: SCIENT_COLORS.gray3,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
})

export const PieChartsRow = styled(FlexContainer)({
  gap: "16px",
  flexWrap: "wrap",
  justifyContent: "center",
})

export const PieChartCard = styled(Card)({
  minWidth: "250px",
  maxWidth: "350px",
})

// AllocationCard styles
export const TotalExposure = styled.div({
  fontSize: "28px",
  fontWeight: 700,
  marginBottom: "12px",
  color: SCIENT_COLORS.cerulean5,
  textAlign: "center",
})

export const AllocationRow = styled(FlexContainer)({
  padding: "8px 0",
  borderBottom: "1px solid #2a2a3e",
  "&:last-child": {
    borderBottom: "none",
  },
})

export const AllocationLabel = styled(FlexContainer)({
  flex: 1,
  gap: "8px",
  alignItems: "center",
})

export const AllocationValue = styled.span({
  fontWeight: 600,
  minWidth: "80px",
  textAlign: "right",
})

export const AllocationPercentage = styled.span({
  color: "#a0a0a0",
  minWidth: "60px",
  textAlign: "right",
})

// Top10Card styles
export const Top10PositionRow = styled(FlexContainer)({
  padding: "6px 0",
  borderBottom: "1px solid #2a2a3e",
  fontSize: "13px",
  "&:last-child": {
    borderBottom: "none",
  },
})

export const Top10Rank = styled.span({
  color: "#a0a0a0",
  minWidth: "24px",
})

export const Top10Symbol = styled.span({
  flex: 1,
  fontWeight: 500,
})

export const Top10Percentage = styled.span({
  minWidth: "50px",
  textAlign: "right",
  color: "#a0a0a0",
})

export const Top10Value = styled.span({
  minWidth: "80px",
  textAlign: "right",
  fontWeight: 600,
})

// GroupCard styles
export const GroupCardContainer = styled.div({
  backgroundColor: SCIENT_COLORS.darkGray2,
  borderRadius: "8px",
  overflow: "hidden",
  border: `1px solid ${SCIENT_COLORS.darkGray4}`,
})

export const GroupHeader = styled(FlexContainer)({
  padding: "12px 16px",
  backgroundColor: SCIENT_COLORS.darkGray3,
  borderBottom: `1px solid ${SCIENT_COLORS.darkGray4}`,
})

export const GroupName = styled.span({
  flex: 1,
  fontWeight: 600,
  fontSize: "14px",
  color: SCIENT_COLORS.white,
})

export const GroupStats = styled(FlexContainer)({
  gap: "16px",
  alignItems: "center",
})

export const GroupPercentage = styled.span({
  color: SCIENT_COLORS.gray3,
  minWidth: "50px",
  textAlign: "right",
  fontSize: "13px",
})

export const GroupValue = styled.span({
  fontWeight: 600,
  minWidth: "80px",
  textAlign: "right",
  fontSize: "13px",
  color: SCIENT_COLORS.cerulean5,
})

export const CompanyRow = styled(FlexContainer)({
  padding: "8px 16px",
  cursor: "pointer",
  borderBottom: `1px solid ${SCIENT_COLORS.darkGray3}`,
  "&:hover": {
    backgroundColor: SCIENT_COLORS.darkGray3,
  },
  "&:last-child": {
    borderBottom: "none",
  },
})

export const CompanyName = styled.span({
  flex: 1,
  fontSize: "13px",
  color: SCIENT_COLORS.gray4,
  marginLeft: 8,
})

export const PositionRow = styled(FlexContainer)({
  padding: "6px 16px 6px 32px",
  fontSize: "12px",
  color: SCIENT_COLORS.gray2,
  backgroundColor: SCIENT_COLORS.darkGray1,
  borderBottom: `1px solid ${SCIENT_COLORS.darkGray2}`,
  "&:last-child": {
    borderBottom: "none",
  },
})

export const PositionSymbol = styled.span({
  flex: 1,
})

export const PositionQuantity = styled.span({
  minWidth: 70,
  textAlign: "right",
  color: SCIENT_COLORS.gray3,
})

export const PositionValue = styled.span({
  fontWeight: 600,
  minWidth: "80px",
  textAlign: "right",
  fontSize: "13px",
  color: SCIENT_COLORS.gray4,
})

// GroupSelector styles
export const SelectorContainer = styled(FlexContainer)({
  padding: "8px 0",
  gap: "4px",
  alignItems: "center",
})

export const SegmentButton = styled.button<{ isActive: boolean }>(({ isActive }) => ({
  padding: "6px 14px",
  fontSize: "12px",
  fontWeight: 500,
  border: "none",
  cursor: "pointer",
  transition: "all 0.15s ease",
  backgroundColor: isActive ? "#3d5a80" : "transparent",
  color: isActive ? "#ffffff" : "#8892a0",
  "&:first-of-type": {
    borderRadius: "4px 0 0 4px",
  },
  "&:last-of-type": {
    borderRadius: "0 4px 4px 0",
  },
  "&:hover": {
    backgroundColor: isActive ? "#3d5a80" : "rgba(61, 90, 128, 0.3)",
    color: "#ffffff",
  },
}))

// SkeletonCard styles
const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.7;
  }
`

export const SkeletonContainer = styled.div<{ height?: number; width?: number }>(
  ({ height, width }) => ({
    backgroundColor: SCIENT_COLORS.darkGray3,
    borderRadius: "8px",
    border: `1px solid ${SCIENT_COLORS.darkGray4}`,
    height: height || 150,
    width: width || "100%",
    minWidth: width || 280,
    flex: width ? "none" : 1,
    animation: `${pulse} 1.5s ease-in-out infinite`,
  }),
)
